import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Cpu, RefreshCw, Save, Trash2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { NewBadge } from '@/components/NewBadge';

export default function PosMappingPage() {
  const [machines, setMachines] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // machine_id → { branch_id, label }
  const [newMachine, setNewMachine] = useState({ machine_id: '', branch_id: '', label: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [mr, br] = await Promise.all([
        api.get('/pos-machines'),
        api.get('/branches'),
      ]);
      setMachines(mr.data || []);
      setBranches(br.data || []);
      const d = {};
      (mr.data || []).forEach(m => { d[m.machine_id] = { branch_id: m.branch_id || '', label: m.label || '' }; });
      setDrafts(d);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (machine_id) => {
    const d = drafts[machine_id] || {};
    if (!d.branch_id) { toast.error('Select a branch'); return; }
    try {
      await api.post('/pos-machines', null, { params: { machine_id, branch_id: d.branch_id, label: d.label || '' } });
      toast.success('Mapping saved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (machine_id) => {
    if (!window.confirm(`Remove mapping for "${machine_id}"?`)) return;
    try {
      await api.delete(`/pos-machines/${encodeURIComponent(machine_id)}`);
      toast.success('Mapping removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const addNew = async () => {
    if (!newMachine.machine_id || !newMachine.branch_id) { toast.error('Machine ID + branch required'); return; }
    try {
      await api.post('/pos-machines', null, {
        params: { machine_id: newMachine.machine_id, branch_id: newMachine.branch_id, label: newMachine.label || '' },
      });
      toast.success('Mapping added');
      setNewMachine({ machine_id: '', branch_id: '', label: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };

  const branchName = (id) => branches.find(b => b.id === id)?.name || '';

  return (
    <DashboardLayout>
      <div className="space-y-4 p-4 max-w-5xl mx-auto" data-testid="pos-mapping-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Cpu size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-outfit flex items-center gap-2">
                POS Machine → Branch Mapping
                <NewBadge since="2026-04-26" featureId="page-pos-mapping" size="xs" />
              </h1>
              <p className="text-xs text-muted-foreground">Map every POS terminal to its physical branch so bank-statement deposits attribute correctly.</p>
            </div>
          </div>
          <Button onClick={load} variant="outline" size="sm" data-testid="pos-mapping-refresh">
            <RefreshCw size={13} className={`mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-outfit">Add new mapping</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <Input placeholder="Machine ID (e.g. POS-001)" value={newMachine.machine_id} onChange={(e) => setNewMachine({ ...newMachine, machine_id: e.target.value })} data-testid="new-machine-id" />
              <Select value={newMachine.branch_id || 'none'} onValueChange={(v) => setNewMachine({ ...newMachine, branch_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="new-machine-branch"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Label (optional)" value={newMachine.label} onChange={(e) => setNewMachine({ ...newMachine, label: e.target.value })} data-testid="new-machine-label" />
              <Button onClick={addNew} className="bg-amber-600 hover:bg-amber-700" data-testid="add-mapping-btn">Add Mapping</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-outfit flex items-center gap-2">
              {machines.length === 0 ? <AlertTriangle size={14} className="text-amber-500" /> : <CheckCircle2 size={14} className="text-emerald-500" />}
              Existing mappings ({machines.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {machines.length === 0 ? (
              <div className="p-8 text-center text-xs text-muted-foreground">
                No POS machines mapped yet. Until you map them, the reconciliation engine falls back to the bank-account's branch.
              </div>
            ) : (
              <div className="divide-y">
                {machines.map(m => {
                  const d = drafts[m.machine_id] || {};
                  return (
                    <div key={m.machine_id} className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 items-center hover:bg-muted/30" data-testid={`machine-row-${m.machine_id}`}>
                      <div className="md:col-span-3 font-mono text-xs">
                        <div className="font-semibold">{m.machine_id}</div>
                        <div className="text-[10px] text-muted-foreground">currently: {branchName(m.branch_id) || '— unmapped —'}</div>
                      </div>
                      <div className="md:col-span-4">
                        <Select value={d.branch_id || 'none'} onValueChange={(v) => setDrafts({ ...drafts, [m.machine_id]: { ...d, branch_id: v === 'none' ? '' : v } })}>
                          <SelectTrigger data-testid={`branch-select-${m.machine_id}`}><SelectValue placeholder="Branch" /></SelectTrigger>
                          <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-3">
                        <Input value={d.label || ''} onChange={(e) => setDrafts({ ...drafts, [m.machine_id]: { ...d, label: e.target.value } })} placeholder="Label (e.g. Counter A)" data-testid={`label-input-${m.machine_id}`} />
                      </div>
                      <div className="md:col-span-2 flex gap-1 justify-end">
                        <Button size="sm" onClick={() => save(m.machine_id)} data-testid={`save-${m.machine_id}`} className="bg-emerald-600 hover:bg-emerald-700">
                          <Save size={12} className="mr-1" />Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(m.machine_id)} data-testid={`delete-${m.machine_id}`} className="text-rose-600">
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
